import React, { createContext, useContext, useState, useMemo } from 'react';
import { plans } from '@/data/pricing';
import { modules } from '@/data/addons';
import { usePricing } from '@/context/PricingContext';

const CART_SESSION_KEY = 'ekklesia_cart';

const VALID_PLANS = new Set(['esencial', 'avanzado']);
const VALID_BILLING = new Set(['monthly', 'annual']);
const VALID_ADDON_IDS = new Set(modules.map((m) => m.id));

function loadCartFromSession() {
  try {
    const raw = sessionStorage.getItem(CART_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      plan: VALID_PLANS.has(parsed.plan) ? parsed.plan : null,
      billing: VALID_BILLING.has(parsed.billing) ? parsed.billing : 'monthly',
      addons: Array.isArray(parsed.addons)
        ? parsed.addons.filter((id) => typeof id === 'string' && VALID_ADDON_IDS.has(id))
        : [],
    };
  } catch {
    return null;
  }
}

function saveCartToSession(plan, billing, addons) {
  try {
    sessionStorage.setItem(CART_SESSION_KEY, JSON.stringify({ plan, billing, addons }));
  } catch { /* ignore */ }
}

const INITIAL_STATE = {
  plan: null,
  billing: 'monthly',
  addons: [],
  isOpen: false,
};

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const pricing = usePricing();
  const [plan, setPlanState] = useState(() => loadCartFromSession()?.plan ?? INITIAL_STATE.plan);
  const [billing, setBillingState] = useState(() => loadCartFromSession()?.billing ?? INITIAL_STATE.billing);
  const [addons, setAddons] = useState(() => loadCartFromSession()?.addons ?? INITIAL_STATE.addons);
  const [isOpen, setIsOpen] = useState(INITIAL_STATE.isOpen);

  // Si billing es annual pero no está disponible, forzar monthly
  const effectiveBilling = (!pricing.annualAvailable && billing === 'annual') ? 'monthly' : billing;

  // Forzar precio fundador activo hasta nuevo aviso (en línea con PricingSection)
  const founderActive = true;

  // Precio base del plan según pricing dinámico
  const basePrice = useMemo(() => {
    if (!plan || !pricing.prices) return 0;
    const p = pricing.prices[plan];
    if (!p) return 0;

    if (effectiveBilling === 'annual') {
      return founderActive
        ? (p.founder_annual_monthly ?? p.founder_monthly)
        : (p.annual_monthly ?? p.monthly);
    }
    return founderActive ? p.founder_monthly : p.monthly;
  }, [plan, effectiveBilling, pricing.prices]);

  // Precio regular (sin descuento fundador) para mostrar tachado
  const regularPrice = useMemo(() => {
    if (!plan || !pricing.prices) return 0;
    const p = pricing.prices[plan];
    if (!p) return 0;

    if (effectiveBilling === 'annual') {
      return p.annual_monthly ?? p.monthly;
    }
    return p.monthly;
  }, [plan, effectiveBilling, pricing.prices]);

  const addonPrice = useMemo(() => {
    if (plan !== 'esencial') return 0;
    return addons.length * pricing.modulePrice;
  }, [plan, addons, pricing.modulePrice]);

  const total = useMemo(() => basePrice + addonPrice, [basePrice, addonPrice]);

  // Cargo real anual
  const annualCharge = useMemo(() => {
    if (!plan || effectiveBilling !== 'annual' || !pricing.prices) return null;
    const p = pricing.prices[plan];
    if (!p) return null;
    const planAnnual = founderActive
      ? (p.founder_annual_total ?? p.founder_monthly * 10)
      : (p.annual_total ?? p.monthly * 10);
    return Math.round((planAnnual + addonPrice * 12) * 100) / 100;
  }, [plan, effectiveBilling, addonPrice, pricing.prices]);

  const planData = useMemo(() => {
    if (!plan) return null;
    return plans.find((p) => p.name.toLowerCase() === plan) || null;
  }, [plan]);

  const addonData = useMemo(() => {
    if (!addons.length) return [];
    return modules.filter((m) => addons.includes(m.id));
  }, [addons]);

  function setPlan(planId) {
    const nextPlan = VALID_PLANS.has(planId) || planId === 'institucional' ? planId : null;
    setPlanState(nextPlan);
    setAddons((prevAddons) => {
      const nextAddons = nextPlan !== 'esencial' ? [] : prevAddons;
      saveCartToSession(nextPlan, effectiveBilling, nextAddons);
      return nextAddons;
    });
    setIsOpen(true);
  }

  function setBilling(b) {
    const val = (!pricing.annualAvailable && b === 'annual') ? 'monthly' : b;
    setBillingState(val);
    setAddons((prevAddons) => {
      saveCartToSession(plan, val, prevAddons);
      return prevAddons;
    });
  }

  function toggleAddon(addonId) {
    if (!VALID_ADDON_IDS.has(addonId)) return;
    setAddons((prev) => {
      const next = prev.includes(addonId)
        ? prev.filter((id) => id !== addonId)
        : [...prev, addonId];
      saveCartToSession(plan, effectiveBilling, next);
      return next;
    });
  }

  function clearCart() {
    setPlanState(INITIAL_STATE.plan);
    setBillingState(INITIAL_STATE.billing);
    setAddons(INITIAL_STATE.addons);
    setIsOpen(false);
    try { sessionStorage.removeItem(CART_SESSION_KEY); } catch { /* ignore */ }
  }

  const value = {
    plan,
    billing: effectiveBilling,
    addons,
    isOpen,
    basePrice,
    regularPrice,
    addonPrice,
    total,
    annualCharge,
    planData,
    addonData,
    setPlan,
    setBilling,
    toggleAddon,
    clearCart,
    setIsOpen,
    // Exponer datos de pricing para componentes
    founderActive,
    annualAvailable: pricing.annualAvailable,
    modulePrice: pricing.modulePrice,
    pricingLoading: pricing.loading,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
